import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { useLanguageStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL || '';

const toSafeStr = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  const localized = getLocalized(val, lang);
  if (typeof localized === 'object' && localized !== null) {
    const firstStr = Object.values(localized).find((v) => typeof v === 'string');
    return firstStr || '';
  }
  return String(localized || '');
};

const extractImgUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object') return img.url || img.secure_url || img.src || '';
  return '';
};

export const BlogPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const journalTitles = {
    en: 'Journal', ru: 'Журнал', nl: 'Blog', ro: 'Blog'
  };

  const noArticlesLabels = {
    en: 'No articles yet. Check back soon!', ru: 'Статей пока нет. Возвращайтесь позже!', nl: 'Nog geen artikelen. Kom binnenkort terug!', ro: 'Niciun articol încă. Reveniți curând!'
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API}/api/blog`);
        if (res.ok) {
          const data = await res.json();
          setPosts(Array.isArray(data) ? data : (data?.posts || []));
        }
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-sans pb-24 select-none" data-testid="blog-page">
      <div className="bg-white pt-28 pb-12 border-b border-[#ECECEC]">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-3 font-medium">{toSafeStr(t?.nav?.blog, 'Blog')}</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-[#0A0A0A]">
              {journalTitles[language] || 'Journal'}
            </h1>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-[#FAFAFA] border border-[#ECECEC] mb-4" />
                <div className="h-3 bg-[#ECECEC] w-1/4 mb-2" />
                <div className="h-6 bg-[#ECECEC] w-3/4" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-[#ECECEC] bg-[#FAFAFA]">
            <p className="text-sm font-light text-[#4A4A4A]">
              {noArticlesLabels[language] || 'No articles yet. Check back soon!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {posts.map((post, index) => {
              const postTitle = toSafeStr(getLocalized(post.title, language), language);
              const postExcerpt = toSafeStr(getLocalized(post.excerpt, language), language);
              const postCat = toSafeStr(post.category, language);
              const featImg = extractImgUrl(post.featured_image || post.image);

              return (
                <motion.article
                  key={post.id || post._id || index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Link
                    to={`/${language}/blog/${post.slug || post.id}`}
                    className="group block"
                    data-testid={`blog-post-${post.slug || index}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#FAFAFA] border border-[#ECECEC] mb-5 shadow-sm">
                      {featImg && (
                        <img
                          src={featImg}
                          alt={postTitle}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-102 pointer-events-none"
                          draggable={false}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-[10px] tracking-wider uppercase text-[#9E9E9E] mb-3 font-medium">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[#9E9E9E]" strokeWidth={1.5} />
                        {post.published_at && new Date(post.published_at).toLocaleDateString()}
                      </span>
                      {postCat && <span className="bg-[#FAFAFA] border border-[#ECECEC] px-2 py-0.5 text-[#4A4A4A] font-normal">{postCat}</span>}
                    </div>
                    <h2 className="text-xl font-light tracking-tight text-[#0A0A0A] mb-2 group-hover:text-[#9E9E9E] transition-colors">
                      {postTitle}
                    </h2>
                    <p className="text-[#4A4A4A] text-sm font-light leading-relaxed line-clamp-2">
                      {postExcerpt}
                    </p>
                  </Link>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const notFoundLabels = {
    en: 'Article not found', ru: 'Статья не найдена', nl: 'Artikel niet gevonden', ro: 'Articolul nu a fost găsit'
  };

  const backToJournalLabels = {
    en: 'Back to Journal', ru: 'Назад в журнал', nl: 'Terug naar Blog', ro: 'Înapoi la Blog'
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/api/blog/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setPost(data);
        }
      } catch (error) {
        console.error('Failed to fetch blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container-custom py-24">
          <div className="max-w-3xl mx-auto animate-pulse">
            <div className="h-4 bg-[#ECECEC] w-1/4 mb-4" />
            <div className="h-12 bg-[#ECECEC] w-3/4 mb-8" />
            <div className="aspect-video bg-[#FAFAFA] mb-8 border border-[#ECECEC]" />
            <div className="space-y-4">
              <div className="h-4 bg-[#ECECEC]" />
              <div className="h-4 bg-[#ECECEC] w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#0A0A0A] select-none">
        <div className="text-center bg-white p-10 border border-[#ECECEC] shadow-sm max-w-sm">
          <h1 className="text-xl font-light mb-4">
            {notFoundLabels[language] || 'Article not found'}
          </h1>
          <Link to={`/${language}/blog`}>
            <Button variant="outline">
              ← {backToJournalLabels[language] || 'Back to Journal'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const postTitle = toSafeStr(getLocalized(post.title, language), language);
  const postExcerpt = toSafeStr(getLocalized(post.excerpt, language), language);
  const postContent = toSafeStr(getLocalized(post.content, language), language);
  const featImg = extractImgUrl(post.featured_image || post.image);

  return (
    <article className="min-h-screen bg-white text-[#0A0A0A] font-sans pb-24 select-none" data-testid="blog-post-page">
      {featImg && (
        <div className="relative h-[45vh] md:h-[50vh] overflow-hidden border-b border-[#ECECEC]">
          <img
            src={featImg}
            alt={postTitle}
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
          <div className="absolute inset-0 bg-black/5" />
        </div>
      )}

      <div className="container-custom py-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <Link to={`/${language}/blog`}>
              <Button variant="outline">
                ← {backToJournalLabels[language] || 'Back to Journal'}
              </Button>
            </Link>
          </div>

          <div className="flex items-center gap-5 text-[10px] tracking-wider uppercase text-[#9E9E9E] mb-6 font-medium">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-[#9E9E9E]" strokeWidth={1.5} />
              {post.published_at && new Date(post.published_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
            {post.author && (
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4 text-[#9E9E9E]" strokeWidth={1.5} />
                {toSafeStr(post.author, language)}
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-5xl font-extralight tracking-tight text-[#0A0A0A] leading-tight mb-6" data-testid="blog-title">
            {postTitle}
          </h1>

          {postExcerpt && (
            <p className="text-base md:text-lg font-light mb-10 text-[#4A4A4A] leading-relaxed border-l border-[#0A0A0A] pl-5">
              {postExcerpt}
            </p>
          )}

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-12 border-b border-[#ECECEC] pb-6">
              <span className="text-[10px] tracking-wider uppercase bg-[#FAFAFA] border border-[#ECECEC] text-[#4A4A4A] px-2.5 py-1 font-medium shadow-sm">
                {post.tags.join(', ')}
              </span>
            </div>
          )}

          <div
            className="prose prose-neutral max-w-none font-light text-[#0A0A0A] leading-relaxed space-y-6
              prose-headings:font-serif prose-headings:font-light prose-headings:tracking-tight prose-headings:text-[#0A0A0A]
              prose-p:text-base prose-p:leading-relaxed prose-p:text-[#0A0A0A]
              prose-a:text-[#0A0A0A] prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-[#9E9E9E] prose-a:transition-colors"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(postContent || '', {
                USE_PROFILES: { html: true },
                FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
                FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
              }),
            }}
          />
        </div>
      </div>
    </article>
  );
};

export default BlogPage;
